import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { PrescriptionUploader } from '../components/prescription-scan/PrescriptionUploader';
import { PrescriptionScanResults } from '../components/prescription-scan/PrescriptionScanResults';

export default function PrescriptionScanPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [showPosWarning, setShowPosWarning] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(selected.type)) return toast.error('Please select an image or PDF file');
    setFile(selected);
    setPreview(selected.type.startsWith('image/') ? URL.createObjectURL(selected) : null);
    setResult(null);
  };
  const clearFile = () => {
    setFile(null); setPreview(null); setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };
  const handleScan = async () => {
    if (!file) return toast.error('Please select a prescription image');
    setScanning(true); setResult(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await api.post('/ai/prescription-scan', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      if (data.validation?.is_valid && data.pos_ready?.can_proceed_to_pos) toast.success('Prescription processed successfully - ready for POS!');
      else if (data.validation?.is_valid) toast.warning('Prescription valid but some items unavailable');
      else toast.error('Prescription validation failed - see issues below');
    } catch (error) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(error, 'Scan failed. Please try a clearer image.');
    } finally { setScanning(false); }
  };
  const proceedToPOS = () => {
    setShowPosWarning(false);
    sessionStorage.setItem('prescription_pos_data', JSON.stringify({
      patientName: result.pos_ready.patient_name || '', cartItems: result.pos_ready.cart_items,
      interactionNotes: result.pos_ready.interaction_notes || '', interactionWarnings: result.pos_ready.interaction_warnings || [],
      prescriptionInfo: { patientAge: result.prescription?.patient_age, doctorName: result.prescription?.doctor_name, prescriptionDate: result.prescription?.prescription_date },
    }));
    toast.success('Sending prescription to POS...');
    navigate('/pos');
  };
  const handleSendToPOS = () => {
    if (!result?.pos_ready?.cart_items?.length) return toast.error('No items available to send to POS');
    if (!result.pos_ready.can_proceed_to_pos) return setShowPosWarning(true);
    proceedToPOS();
  };

  return <div className="max-w-6xl mx-auto space-y-6" data-testid="prescription-scan-page">
    <div><h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>AI Prescription Scanner</h1><p className="text-sm text-muted-foreground mt-1">Upload a prescription for validation, drug interaction check, and inventory verification</p></div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <PrescriptionUploader file={file} preview={preview} scanning={scanning} onFileSelect={handleFileSelect} onClear={clearFile} onScan={handleScan} fileInputRef={fileInputRef} cameraInputRef={cameraInputRef} />
      <PrescriptionScanResults result={result} scanning={scanning} showPosWarning={showPosWarning} onWarningChange={setShowPosWarning} onSendToPOS={handleSendToPOS} onProceedToPOS={proceedToPOS} />
    </div>
  </div>;
}